/*
 * Phase 1 Trade Expansion — cohort trade panel.
 *
 * Renders the guided setup UI for a supported non-Roofing cohort trade
 * (Flooring / Painting / Drywall / Siding / Insulation). Reuses the
 * existing pricing rail: for each canonical system the contractor can
 * choose a pricing_method, enter their price, save, and approve. The
 * saved rule flows through the existing PricingRule + estimate-picker
 * pipeline. Custom / One-Off entries land under the trade's registered
 * custom prefix.
 *
 * Roofing keeps its own dedicated rendering path — this component is
 * only used when the selected trade is not Roofing.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckCircle2, ChevronRight, AlertTriangle } from "lucide-react";
import { useLang } from "@/context/LangContext";
// PO 2026-02 · Trade Setup required-field UX. One shared helper maps
// backend readiness tokens to contractor-facing EN/ES labels so raw
// snake_case tokens never leak to the toast or inline highlights.
import {
  friendlyFieldLabel,
  buildMissingSummary,
  extractMissingFromError,
} from "@/lib/tradeSetupFieldLabels";

const PRICING_METHODS = [
  { key: "installed_selling", label: "All-in Price — Materials + Labor + Profit Included" },
  { key: "materials_plus_labor", label: "Materials + Labor Separately" },
  { key: "materials_only", label: "Materials only" },
  { key: "labor_only", label: "Labor only" },
  { key: "flat", label: "Flat Price for the Whole Job" },
  { key: "time_and_materials", label: "Time & Materials" },
  { key: "sub_quote_plus_markup", label: "Subcontractor quote + markup" },
  { key: "per_job", label: "Per-job pricing" },
];

const DEFAULT_UNIT_BY_TRADE = {
  Flooring: "sqft",
  Painting: "sqft",
  Drywall: "sqft",
  Siding: "sqft",
  Insulation: "sqft",
};

// Reverse-map the persisted canonical shape (what buildPayload sends and
// what /trade/{trade} echoes back) into the contractor-facing UI method
// key the <select> and the method-specific JSX branches use.
//
// buildPayload rewrites some UI keys before persistence:
//   installed_selling         -> pricing_method="per_unit",
//                                pricing_structure="complete_installed"
//   materials_plus_labor      -> pricing_method="materials_labor",
//                                material_source="flat_amount",
//                                labor_approach="flat_amount"
//   materials_only            -> pricing_method="materials_labor",
//                                material_source="flat_amount",
//                                labor_approach="included_in_material"
//   labor_only                -> pricing_method="materials_labor",
//                                material_source="customer_supplied",
//                                labor_approach="flat_amount"
//   sub_quote_plus_markup     -> pricing_method="sub_markup"
//   per_job                   -> pricing_structure="per_job"
//   flat / time_and_materials -> unchanged
//
// Without this reverse map the second render (post-save hydration) sees
// a canonical value like "per_unit" that matches no UI branch, so the
// method-specific controls disappear even though the data is intact.
const UI_METHOD_KEYS = [
  "installed_selling", "materials_plus_labor", "materials_only",
  "labor_only", "flat", "time_and_materials", "sub_quote_plus_markup",
  "per_job",
];
const deriveUiMethod = (meta) => {
  const pm = (meta && meta.pricing_method) || "";
  const ps = (meta && meta.pricing_structure) || "";
  if (!pm && !ps) return "installed_selling";
  if (pm === "per_unit" && ps === "complete_installed") return "installed_selling";
  if (ps === "per_job" || pm === "per_job") return "per_job";
  if (pm === "sub_markup") return "sub_quote_plus_markup";
  if (pm === "materials_labor") {
    const src = (meta && meta.material_source) || "";
    const lab = (meta && meta.labor_approach) || "";
    if (src === "customer_supplied") return "labor_only";
    if (lab === "included_in_material") return "materials_only";
    return "materials_plus_labor";
  }
  return UI_METHOD_KEYS.indexOf(pm) !== -1 ? pm : "installed_selling";
};

const CohortTradePanel = ({ trade }) => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [openSystem, setOpenSystem] = useState(null);

  const refresh = async () => {
    const r = await api.get(`/onboarding/trade-setup/trade/${encodeURIComponent(trade)}`);
    setData(r.data);
  };

  useEffect(() => { refresh(); /* eslint-disable-line */ }, [trade]);

  if (!data) return <div className="text-xs text-[var(--jc-text-soft)] p-3">Loading {trade} setup…</div>;
  if (!data.available) {
    return (
      <div className="text-xs text-[var(--jc-text-soft)] p-3"
           data-testid={`cohort-trade-${trade}-unavailable`}>
        {data.message || "Trade setup will be available in the next expansion."}
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-[var(--jc-border)] pt-3 space-y-2"
         data-testid={`cohort-trade-${trade}-panel`}>
      <div className="text-xs text-[var(--jc-text-soft)]"
           data-testid={`cohort-trade-${trade}-intro`}>
        {t("cohort_trade_setup.intro")}
      </div>
      {/* PO 2026-02 · Pricing completeness clarity — reassures the
          contractor that unused canonical systems don't block setup.
          Copy-only, no schema, no logic changes. */}
      <div
        data-testid={`cohort-trade-${trade}-optional-helper`}
        className="text-xs text-[var(--jc-text-soft)] border-l-4 border-l-sky-400 bg-sky-50/50 px-3 py-2 rounded"
      >
        {t("soyb.systems_optional_helper")}
      </div>
      {(data.systems || []).map((sys) => (
        <SystemCard key={sys.system_type}
                    trade={trade}
                    system={sys}
                    open={openSystem === sys.system_type}
                    onToggle={() => setOpenSystem(openSystem === sys.system_type ? null : sys.system_type)}
                    onSaved={refresh} />
      ))}
      <CustomCard trade={trade} onSaved={refresh} />
    </div>
  );
};

const SystemCard = ({ trade, system, open, onToggle, onSaved }) => {
  const { t } = useLang();
  const readiness = system?.readiness?.kind || "not_started";
  const label = system.system_type;
  const approved = readiness === "ready" || readiness === "ready_with_per_job_pricing";
  return (
    <div className="jc-card p-3 border-l-4"
         style={{ borderLeftColor: approved ? "#16a34a" : "var(--jc-border-strong)" }}
         data-testid={`cohort-system-${trade}-${label}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{label}</div>
          <div className="text-xs text-[var(--jc-text-soft)]">
            {readiness === "ready" && (<><CheckCircle2 size={12} className="inline mr-1" />{t("cohort_trade_setup.approved")}</>)}
            {readiness === "ready_with_per_job_pricing" && t("cohort_trade_setup.approved_per_job")}
            {readiness === "not_started" && t("cohort_trade_setup.not_started")}
            {readiness === "in_progress" && t("cohort_trade_setup.in_progress")}
          </div>
        </div>
        <button onClick={onToggle}
                className="text-xs px-2 py-1 rounded border border-[var(--jc-border-strong)]"
                data-testid={`cohort-system-${trade}-${label}-toggle`}>
          {open ? "Close" : approved ? "Edit" : "Configure"} <ChevronRight size={10} className="inline" />
        </button>
      </div>
      {open && (
        <SystemForm trade={trade} systemType={label}
                    meta={system.meta || {}}
                    onSaved={onSaved} />
      )}
    </div>
  );
};

const SystemForm = ({ trade, systemType, meta, onSaved }) => {
  const { t, lang } = useLang();
  const [f, setF] = useState({
    pricing_method: deriveUiMethod(meta),
    selling_price_per_unit: meta.selling_price_per_unit ?? "",
    selling_price_unit: meta.selling_price_unit || DEFAULT_UNIT_BY_TRADE[trade] || "sqft",
    flat_price: meta.flat_price ?? "",
    labor_amount: meta.labor_amount ?? "",
    material_amount: (meta.material_total ?? meta.material_amount) ?? "",
    markup_pct: (meta.markup_percent ?? meta.markup_pct) ?? "",
    hourly_rate: meta.hourly_rate ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  // PO 2026-02 · Track which specific fields the backend flagged as
  // incomplete so we can inline-highlight them and build a friendly
  // toast summary. Cleared on every save/approve attempt.
  const [missingFields, setMissingFields] = useState([]);
  const missingSet = new Set(missingFields);
  const errCls = (token) =>
    missingSet.has(token)
      ? "ring-2 ring-red-400 border-red-400"
      : "";

  const buildPayload = () => {
    const payload = {
      selected: true,
      pricing_method: f.pricing_method,
    };
    if (f.pricing_method === "installed_selling") {
      // Map to the dispatcher's canonical shape used by the locked
      // Roofing pilot rail.
      payload.pricing_method = "per_unit";
      payload.pricing_structure = "complete_installed";
      payload.selling_price_per_unit = f.selling_price_per_unit === "" ? null : Number(f.selling_price_per_unit);
      payload.selling_price_unit = f.selling_price_unit;
      payload.markup_handling = "built_in";
      payload.material_source = "per_job";
      payload.labor_approach = "included_in_installed";
    } else if (f.pricing_method === "flat") {
      payload.flat_price = f.flat_price === "" ? null : Number(f.flat_price);
      payload.markup_handling = "built_in";
    } else if (f.pricing_method === "materials_plus_labor") {
      payload.pricing_method = "materials_labor";
      payload.material_source = "flat_amount";
      payload.material_total = f.material_amount === "" ? null : Number(f.material_amount);
      payload.labor_approach = "flat_amount";
      payload.labor_amount = f.labor_amount === "" ? null : Number(f.labor_amount);
      payload.markup_handling = f.markup_pct === "" ? "built_in" : "flat_percent";
      payload.markup_percent = f.markup_pct === "" ? null : Number(f.markup_pct);
    } else if (f.pricing_method === "materials_only") {
      payload.pricing_method = "materials_labor";
      payload.material_source = "flat_amount";
      payload.material_total = f.material_amount === "" ? null : Number(f.material_amount);
      payload.labor_approach = "included_in_material";
      payload.markup_handling = f.markup_pct === "" ? "built_in" : "flat_percent";
      payload.markup_percent = f.markup_pct === "" ? null : Number(f.markup_pct);
    } else if (f.pricing_method === "labor_only") {
      payload.pricing_method = "materials_labor";
      payload.material_source = "customer_supplied";
      payload.labor_approach = "flat_amount";
      payload.labor_amount = f.labor_amount === "" ? null : Number(f.labor_amount);
      payload.markup_handling = f.markup_pct === "" ? "built_in" : "flat_percent";
      payload.markup_percent = f.markup_pct === "" ? null : Number(f.markup_pct);
    } else if (f.pricing_method === "time_and_materials") {
      payload.hourly_rate = f.hourly_rate === "" ? null : Number(f.hourly_rate);
      payload.markup_handling = f.markup_pct === "" ? "built_in" : "flat_percent";
      payload.markup_percent = f.markup_pct === "" ? null : Number(f.markup_pct);
    } else if (f.pricing_method === "sub_quote_plus_markup") {
      payload.pricing_method = "sub_markup";
      payload.markup_handling = f.markup_pct === "" ? "built_in" : "flat_percent";
      payload.markup_percent = f.markup_pct === "" ? null : Number(f.markup_pct);
    } else if (f.pricing_method === "per_job") {
      payload.pricing_structure = "per_job";
      payload.markup_handling = "per_job";
    }
    return payload;
  };

  const friendlyError = (e) => {
    const missing = extractMissingFromError(e);
    if (missing.length) {
      setMissingFields(missing);
      // Try to bring the first missing input into view so the
      // contractor sees the highlight without hunting.
      setTimeout(() => {
        const el = document.querySelector(
          `[data-testid="cohort-form-${trade}-${systemType}-${missing[0].replace(/_/g,'-')}"]`
        );
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 60);
      return buildMissingSummary(missing, lang);
    }
    const d = e && e.response && e.response.data && e.response.data.detail;
    if (typeof d === "string" && d.indexOf("system_not_selected") !== -1) {
      return t("cohort_trade_setup.err_not_saved");
    }
    return (typeof d === "string" && d) || t("cohort_trade_setup.err_generic");
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setMissingFields([]);
    try {
      await api.put(
        `/onboarding/trade-setup/trade/${encodeURIComponent(trade)}/system/${encodeURIComponent(systemType)}`,
        buildPayload(),
      );
      toast.success(t("cohort_trade_setup.saved_toast"));
      onSaved && onSaved();
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (approving) return;
    setApproving(true);
    setMissingFields([]);
    try {
      // Save first — the "Save and approve" button is a single
      // contractor-facing operation. Any pending form edits are
      // persisted here before approval so the readiness resolver
      // sees the current values.
      await api.put(
        `/onboarding/trade-setup/trade/${encodeURIComponent(trade)}/system/${encodeURIComponent(systemType)}`,
        buildPayload(),
      );
      await api.post(
        `/onboarding/trade-setup/trade/${encodeURIComponent(trade)}/system/${encodeURIComponent(systemType)}/approve`,
      );
      toast.success(t("cohort_trade_setup.approved_toast"));
      onSaved && onSaved();
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setApproving(false);
    }
  };

  const pm = f.pricing_method;
  return (
    <div className="mt-3 space-y-2"
         data-testid={`cohort-form-${trade}-${systemType}`}>
      {/* PO 2026-02 · Friendly required-field summary. Only renders
          after the backend flags missing fields; each affected input
          also picks up an inline red ring via errCls(). */}
      {missingFields.length > 0 && (
        <div
          data-testid={`cohort-form-${trade}-${systemType}-missing-summary`}
          className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>{buildMissingSummary(missingFields, lang)}</div>
        </div>
      )}
      <label className="block text-xs font-semibold">
        {friendlyFieldLabel("pricing_method", lang)}
        <select value={pm}
                onChange={(e) => { setF({ ...f, pricing_method: e.target.value }); setMissingFields([]); }}
                className={`mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm ${errCls("pricing_method")}`}
                data-testid={`cohort-form-${trade}-${systemType}-pricing-method`}>
          {PRICING_METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </label>
      {pm === "installed_selling" && (
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">Selling price per unit
            <input value={f.selling_price_per_unit} type="number" min="0" step="0.01"
                   onChange={(e) => setF({ ...f, selling_price_per_unit: e.target.value })}
                   className="mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                   data-testid={`cohort-form-${trade}-${systemType}-selling-price`} />
          </label>
          <label className="text-xs">Unit
            <input value={f.selling_price_unit}
                   onChange={(e) => setF({ ...f, selling_price_unit: e.target.value })}
                   className="mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                   data-testid={`cohort-form-${trade}-${systemType}-unit`} />
          </label>
        </div>
      )}
      {pm === "flat" && (
        <label className="block text-xs">Flat price
          <input value={f.flat_price} type="number" min="0" step="0.01"
                 onChange={(e) => setF({ ...f, flat_price: e.target.value })}
                 className="mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                 data-testid={`cohort-form-${trade}-${systemType}-flat`} />
        </label>
      )}
      {(pm === "materials_plus_labor" || pm === "materials_only" || pm === "labor_only") && (
        <div className="grid grid-cols-2 gap-2">
          {(pm === "materials_plus_labor" || pm === "materials_only") && (
            <label className="text-xs">Material
              <input value={f.material_amount} type="number" min="0" step="0.01"
                     onChange={(e) => setF({ ...f, material_amount: e.target.value })}
                     className="mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                     data-testid={`cohort-form-${trade}-${systemType}-material`} />
            </label>
          )}
          {(pm === "materials_plus_labor" || pm === "labor_only") && (
            <label className="text-xs">Labor
              <input value={f.labor_amount} type="number" min="0" step="0.01"
                     onChange={(e) => setF({ ...f, labor_amount: e.target.value })}
                     className="mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                     data-testid={`cohort-form-${trade}-${systemType}-labor`} />
            </label>
          )}
          <label className="text-xs">Markup %
            <input value={f.markup_pct} type="number" min="0" step="0.1"
                   onChange={(e) => setF({ ...f, markup_pct: e.target.value })}
                   className="mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                   data-testid={`cohort-form-${trade}-${systemType}-markup`} />
          </label>
        </div>
      )}
      {pm === "time_and_materials" && (
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">Hourly rate
            <input value={f.hourly_rate} type="number" min="0" step="0.01"
                   onChange={(e) => setF({ ...f, hourly_rate: e.target.value })}
                   className="mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                   data-testid={`cohort-form-${trade}-${systemType}-hourly-rate`} />
          </label>
          <label className="text-xs">Markup %
            <input value={f.markup_pct} type="number" min="0" step="0.1"
                   onChange={(e) => setF({ ...f, markup_pct: e.target.value })}
                   className="mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                   data-testid={`cohort-form-${trade}-${systemType}-tm-markup`} />
          </label>
        </div>
      )}
      {pm === "sub_quote_plus_markup" && (
        <label className="block text-xs">Markup % on subcontractor quote
          <input value={f.markup_pct} type="number" min="0" step="0.1"
                 onChange={(e) => setF({ ...f, markup_pct: e.target.value })}
                 className="mt-1 block w-full rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                 data-testid={`cohort-form-${trade}-${systemType}-sub-markup`} />
        </label>
      )}
      {pm === "per_job" && (
        <div className="text-xs text-[var(--jc-text-soft)] p-2 rounded bg-[var(--jc-bg)] border border-[var(--jc-border)]">
          Per-job pricing — you'll set the price on each estimate.
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <button onClick={save} disabled={saving}
                className="text-xs px-3 py-1.5 rounded border border-[var(--jc-border-strong)] disabled:opacity-60"
                data-testid={`cohort-form-${trade}-${systemType}-save`}>
          {saving ? "…" : t("cohort_trade_setup.save")}
        </button>
        <button onClick={approve} disabled={approving}
                className="text-xs px-3 py-1.5 rounded jc-btn-primary disabled:opacity-60"
                data-testid={`cohort-form-${trade}-${systemType}-approve`}>
          {approving ? "…" : t("cohort_trade_setup.save_and_approve")}
        </button>
      </div>
    </div>
  );
};

const CustomCard = ({ trade, onSaved }) => {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [usage, setUsage] = useState("reusable");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (saving || !name.trim()) return;
    setSaving(true);
    try {
      await api.post(`/onboarding/trade-setup/trade/${encodeURIComponent(trade)}/system/custom`,
                     { display_name: name.trim(), custom_usage: usage });
      toast.success("Custom system added");
      setName("");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="jc-card p-3 mt-2"
         data-testid={`cohort-custom-${trade}`}>
      <div className="text-xs font-semibold mb-1">{t("cohort_trade_setup.custom_title")} — {trade}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input value={name} placeholder={t("cohort_trade_setup.custom_placeholder")}
               onChange={(e) => setName(e.target.value)}
               className="rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm sm:col-span-2"
               data-testid={`cohort-custom-${trade}-name`} />
        <select value={usage} onChange={(e) => setUsage(e.target.value)}
                className="rounded border border-[var(--jc-border-strong)] px-2 py-1.5 text-sm"
                data-testid={`cohort-custom-${trade}-usage`}>
          <option value="reusable">{t("cohort_trade_setup.custom_reusable")}</option>
          <option value="one_off">{t("cohort_trade_setup.custom_one_off")}</option>
        </select>
      </div>
      <button onClick={submit} disabled={saving || !name.trim()}
              className="mt-2 text-xs px-3 py-1.5 rounded jc-btn-primary disabled:opacity-60"
              data-testid={`cohort-custom-${trade}-add`}>
        {saving ? "…" : t("cohort_trade_setup.custom_add")}
      </button>
    </div>
  );
};

export default CohortTradePanel;
